// @flow

/**
 * CSV Export Button
 * Generates and downloads a CSV file from tabular data
 */

import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Icon } from '@blueprintjs/core';

const CsvButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background: transparent;
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: rgba(0, 0, 0, 0.7);
  }
`;

type Column = {
  key: string,
  header: string,
};

type Props = {
  title: string,
  columns: Column[],
  rows: Object[],
};

function escapeCsvValue(value: any): string {
  const str = value === null || value === undefined ? '' : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

const ExportCsvButton = ({ title, columns, rows }: Props) => {
  const handleExport = useCallback(() => {
    const headerRow = columns.map(c => escapeCsvValue(c.header)).join(',');
    const dataRows = rows.map(row =>
      columns.map(c => escapeCsvValue(row[c.key])).join(',')
    );
    const csv = [headerRow, ...dataRows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/[^a-zA-Z0-9 -]/g, '').trim()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [title, columns, rows]);

  return (
    <CsvButton onClick={handleExport} title="Export as CSV">
      <Icon icon="export" iconSize={12} />
      CSV
    </CsvButton>
  );
};

export default ExportCsvButton;
